import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import type { DocumentSnapshot } from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { getQueryOnce } from 'shared/services/firestore';
import {
  DEFAULT_RATE_CARD_HOURLY_RATE_MICRO_USD,
  mapRateCard,
  RateCard,
} from 'shared/types/rateCard';
import { mapCustomer, Customer } from 'domains/customers/model/customer';
import { deletePurchaseOrderDocumentsForCustomer } from 'domains/billing/data/purchaseOrders';

/**
 * `customers` + `rate_cards` — ported from aligned's `ManagementService`
 * (`management_service.dart:50-118`). A customer is the org/billing entity a
 * `Manager` belongs to (`services/managers.ts`).
 */

const CUSTOMERS = 'customers';
const RATE_CARDS = 'rate_cards';

/** aligned's `kdDefaultHourlyRate` as imported by `management_service.dart`
 *  (`app/app_constants.dart`, $50/hr) — a DIFFERENT constant from the
 *  expert-side $20/hr task-earnings fallback/onboarding floor; this one is
 *  the default customer billing rate. */
export { DEFAULT_RATE_CARD_HOURLY_RATE_MICRO_USD };

export function getCustomer(id: string): Promise<Customer | null> {
  return getDoc(doc(db, CUSTOMERS, id)).then((snap) =>
    snap.exists() ? mapCustomer(snap.id, snap.data()) : null,
  );
}

/** Ported from aligned's broad admin/customer dropdown reads. */
export async function getAllCustomers(): Promise<Customer[]> {
  const snap = await getDocs(collection(db, CUSTOMERS));
  return snap.docs.map((d) => mapCustomer(d.id, d.data()));
}

/** Read all rate cards for a customer. Consumers sort client-side to avoid index coupling. */
export function getRateCardsForCustomer(customerId: string): Promise<RateCard[]> {
  return getQueryOnce(
    query(collection(db, RATE_CARDS), where('customerId', '==', customerId)),
    mapRateCard,
  );
}

export interface NewRateCardInput {
  customerId: string;
  name: string;
  description?: string | null;
  hourlyRate: number;
  isDefault?: boolean;
}

/** Create a customer rate card, optionally making it the sole default rate card. */
export async function createRateCard(input: NewRateCardInput): Promise<RateCard> {
  const rateCardRef = doc(collection(db, RATE_CARDS));
  const data = {
    id: rateCardRef.id,
    customerId: input.customerId,
    name: input.name,
    description: input.description ?? null,
    hourlyRate: input.hourlyRate,
    isDefault: input.isDefault === true,
  };
  await setDoc(rateCardRef, data);
  const created = mapRateCard(rateCardRef.id, data);
  if (created.isDefault) await setDefaultRateCard(created);
  return created;
}

/** Matches aligned's default-rate-card action: clear all customer defaults, then set this one. */
export async function setDefaultRateCard(rateCard: RateCard): Promise<void> {
  const rateCards = await getRateCardsForCustomer(rateCard.customerId);
  await Promise.all(
    rateCards.map((current) =>
      setDoc(doc(db, RATE_CARDS, current.id), { isDefault: current.id === rateCard.id }, { merge: true }),
    ),
  );
  await setDoc(doc(db, CUSTOMERS, rateCard.customerId), { defaultRateCardId: rateCard.id }, { merge: true });
}

export interface RateCardUpdate {
  name?: string | null;
  description?: string | null;
  hourlyRate?: number | null;
}

export function updateRateCard(rateCardId: string, fields: RateCardUpdate): Promise<void> {
  return setDoc(doc(db, RATE_CARDS, rateCardId), fields, { merge: true });
}

export async function deleteRateCard(rateCard: RateCard): Promise<void> {
  // Aligned intentionally deletes only the rate-card document. It does not
  // select a replacement or repair `customers.defaultRateCardId` here; keep
  // that source behavior and let the next explicit default action establish a
  // new pointer.
  await deleteDoc(doc(db, RATE_CARDS, rateCard.id));
}

export interface NewCustomerInput {
  name: string;
  contactEmail: string;
  contactName?: string | null;
  billingEmail?: string | null;
  contactPhone?: string | null;
  primaryManagerId?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  dataSetPrefix?: string | null;
  postalCode?: string | null;
}

/**
 * Creates a customer, then a default rate card for it, then stamps
 * `defaultRateCardId` back onto the customer — matches aligned's
 * `createCustomer` exactly (`management_service.dart:76-118`): create →
 * update-with-generated-id (twice, once per doc) → update the customer again
 * with the rate card id.
 */
export async function createCustomer(input: NewCustomerInput): Promise<Customer> {
  const customerRef = doc(collection(db, CUSTOMERS));
  const customerData = {
    id: customerRef.id,
    name: input.name,
    contactEmail: input.contactEmail,
    contactName: input.contactName ?? null,
    billingEmail: input.billingEmail ?? null,
    contactPhone: input.contactPhone ?? null,
    primaryManagerId: input.primaryManagerId ?? null,
    streetAddress: input.streetAddress ?? null,
    city: input.city ?? null,
    state: input.state ?? null,
    country: input.country ?? null,
    dataSetPrefix: input.dataSetPrefix ?? null,
    postalCode: input.postalCode ?? null,
    blacklistedCountryCodes: [],
  };
  await setDoc(customerRef, customerData);

  const rateCardRef = doc(collection(db, RATE_CARDS));
  const rateCardData = {
    id: rateCardRef.id,
    customerId: customerRef.id,
    name: 'Default Rate Card',
    description: `Default rate card for ${input.name}`,
    hourlyRate: DEFAULT_RATE_CARD_HOURLY_RATE_MICRO_USD,
    isDefault: true,
  };
  await setDoc(rateCardRef, rateCardData);

  const updated = { ...customerData, defaultRateCardId: rateCardRef.id };
  await setDoc(customerRef, updated, { merge: true });

  return mapCustomer(customerRef.id, updated);
}

/** Plain merge-update, matches `ManagementService.updateCustomer` (mirrors `services/managers.ts:updateManager`). */
export function updateCustomer(customerId: string, fields: Partial<Customer>): Promise<void> {
  return setDoc(doc(db, CUSTOMERS, customerId), fields, { merge: true });
}

/** Count non-deleted jobs before a customer cascade, exactly as aligned does. */
export async function getJobCountForCustomer(customerId: string): Promise<number> {
  const snap = await getDocs(query(collection(db, 'jobs'), where('customerId', '==', customerId), where('deletedAt', '==', null)));
  return snap.size;
}

export function deleteCustomer(customerId: string): Promise<void> {
  return deleteDoc(doc(db, CUSTOMERS, customerId));
}

export interface CascadeDeleteCustomerResult {
  success: boolean;
  attachedJobCount: number;
  managerCount: number;
  rateCardCount: number;
  purchaseOrderCount: number;
}

/**
 * Port of aligned's `ManagementService.cascadeDeleteCustomer`: refuse a
 * destructive delete when any non-deleted jobs remain, then remove managers,
 * rate cards, purchase orders, customer PO files, and finally the customer.
 */
export async function cascadeDeleteCustomer(customerId: string): Promise<CascadeDeleteCustomerResult> {
  const attachedJobCount = await getJobCountForCustomer(customerId);
  if (attachedJobCount > 0) {
    return { success: false, attachedJobCount, managerCount: 0, rateCardCount: 0, purchaseOrderCount: 0 };
  }

  const [managerSnap, rateCardSnap, purchaseOrderSnap] = await Promise.all([
    getDocs(query(collection(db, 'managers'), where('customerId', '==', customerId))),
    getDocs(query(collection(db, RATE_CARDS), where('customerId', '==', customerId))),
    getDocs(query(collection(db, 'purchase_orders'), where('customerId', '==', customerId))),
  ]);

  // Keep user denormalized manager/customer arrays in sync before deleting the
  // manager docs (the same cleanup performed by FirestoreApiService).
  // Delete managers sequentially, as aligned does. This matters when one user
  // owns multiple managers: parallel read-modify-writes would race and could
  // restore an already-removed manager/customer id.
  for (const manager of managerSnap.docs) {
    const managerData = manager.data() as Record<string, unknown>;
    const userAccountId = typeof managerData.userAccountId === 'string' ? managerData.userAccountId : null;
    const firebaseId = typeof managerData.firebaseId === 'string' ? managerData.firebaseId : null;
    const userMatches = userAccountId
      ? await getDocs(query(collection(db, 'users'), where('userAccountId', '==', userAccountId)))
      : null;
    let userRef = userMatches?.docs[0]?.ref;
    let userSnap: DocumentSnapshot | undefined = userMatches?.docs[0];
    if (!userSnap && firebaseId) {
      userRef = doc(db, 'users', firebaseId);
      userSnap = await getDoc(userRef);
    }
    if (!userRef || !userSnap?.exists()) {
      throw new Error(`No user record found for manager ${manager.id}.`);
    }
    const userData = userSnap.data() as Record<string, unknown>;
    const managerIds = Array.isArray(userData.managerIds) ? userData.managerIds.filter((id): id is string => typeof id === 'string' && id !== manager.id) : [];
    const customerIds = Array.isArray(userData.customerIds) ? userData.customerIds.filter((id): id is string => typeof id === 'string' && id !== customerId) : [];
    await updateDoc(userRef, { managerIds, customerIds });
    await deleteDoc(manager.ref);
  }
  await Promise.all([
    ...rateCardSnap.docs.map((item) => deleteDoc(item.ref)),
    ...purchaseOrderSnap.docs.map((item) => deleteDoc(item.ref)),
  ]);
  await deletePurchaseOrderDocumentsForCustomer(customerId);
  await deleteCustomer(customerId);
  return {
    success: true,
    attachedJobCount: 0,
    managerCount: managerSnap.size,
    rateCardCount: rateCardSnap.size,
    purchaseOrderCount: purchaseOrderSnap.size,
  };
}
